import { css, html, TemplateResult, unsafeCSS } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { Api } from "../api/Api.js";
import { Engine } from "../engine/Engine.js";
import lfUrl from "../fonts/lg.ttf";
import { BaseElement } from "../ui/base/BaseElement.js";
import { waitTill } from "../util/Wait.js";
import { logger } from "../util/Logger.js";
import {
  setConfiguratorData,
  ConfiguratorVariant,
} from "./ConfiguratorData.js";
import { configuratorSVGs } from "./SugarConfiguratorSVGs.js";
import type { SugarConfiguratorViewerEntity } from "./SugarConfiguratorViewer.js";
import "./SugarConfiguratorViewer.js";
import "../ui/LoadingElement.js";
import "../ui/LoadingElement2.js";
import("../postporcesses/GTAOPostProcessElement.js");
import("../postporcesses/SSGIPostProcessElement.js");
import("../postporcesses/ProgressiveLightmapGroundElement.js");
import("../camera/CameraAnimationElement");
import("../configurator/SmoothOrbitElement.js");

const modelViewerStyles = css`
  @font-face {
    font-family: "lg";
    src: url("${unsafeCSS(lfUrl)}") format("truetype");
  }

  :host {
    display: block;
    box-sizing: border-box;
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 200px;
    aspect-ratio: 1.15;
  }

  .model-viewer-root {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .mv-loading-layer {
    position: absolute;
    inset: 0;
    z-index: 20;
    pointer-events: none;
  }

  slot[name="loading"]::slotted(*) {
    pointer-events: auto;
  }

  sugar-canvas {
    width: 100%;
    height: 100%;
    display: block;
    opacity: 1;
    position: relative;
  }

  .sugar-meter-button {
    position: absolute;
    bottom: 10px;
    left: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 0px solid black;
    background-color: #eeeeee;
  }

  .sugar-meter-button.active {
    background-color: black;
  }

  .sugar-meter-button.active > .sugar-meter-button-svg {
    background: url("${unsafeCSS(configuratorSVGs.meterButtonActive32)}");
  }

  .sugar-meter-button-svg {
    width: 32px;
    height: 32px;
    border: none;
    background: url("${unsafeCSS(configuratorSVGs.meterButton32)}");
  }

  .sugar-3d-button-animate {
    background: url("${unsafeCSS(configuratorSVGs.button3DAnimate36)}");
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 36px;
    border: none;
    position: absolute;
  }

  .model-viewer-overlay-stack {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
  }

  .model-viewer-overlay-stack slot[name="overlay"]::slotted(*) {
    pointer-events: auto;
  }

  slot[name="controls"]::slotted(*) {
    pointer-events: auto;
  }

  .model-viewer-default-controls {
    pointer-events: none;
    position: absolute;
    inset: 0;
  }

  .model-viewer-default-controls .sugar-3d-button-animate,
  .model-viewer-default-controls .sugar-meter-button {
    pointer-events: auto;
  }

  @media (min-width: 999px) {
    :host {
      aspect-ratio: 1.33;
    }

    .sugar-meter-button.active > .sugar-meter-button-svg {
      background: url("${unsafeCSS(configuratorSVGs.meterButtonActive40)}");
    }

    .sugar-meter-button-svg {
      width: 40px;
      height: 40px;
      border: none;
      background: url("${unsafeCSS(configuratorSVGs.meterButton40)}");
    }

    .sugar-3d-button-animate {
      background: url("${unsafeCSS(configuratorSVGs.button3DAnimate80)}");
      bottom: 0px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 80px;
      border: none;
      position: absolute;
    }
  }
`;

export type ModelViewerLoadingPhase =
  | "fetch-start"
  | "fetch-done"
  | "variant-pending"
  | "scene-ready";

/**
 * Bağımsız 3D konfigüratör canvas yığını (sugar-configurator-viewer dosyasına dokunulmadan).
 * Google &lt;model-viewer&gt; ile aynı sayfada kullanmayın (isim çakışması).
 *
 * Slotlar: `loading` (varsayılan: loading-element + loading-element2), `controls` (animate + metre),
 * `overlay` (ek UI). Özel içerik verildiğinde `model-viewer-loading` olayını dinleyerek kendi
 * yükleme durumunuzu yönetebilirsiniz.
 *
 * Varsayılan varyant alanları: `variant-attributes` HTML’de JSON string (`{"body":"...","armType":"..."}` gibi
 * istediğiniz anahtarlar) veya JS’te `defaultAttributes` nesnesi. `changeVariant` / tam `variant` atanması
 * bu tabanın üzerine yazar.
 */
@customElement("model-viewer")
export class ConfiguratorModelViewer extends BaseElement {
  static styles = modelViewerStyles;
  protected override useShadowRoot = true;

  @property({ type: String, attribute: "configurator-id" })
  configuratorId = "24";

  /**
   * HTML: `variant-attributes='{"body":"...","özelAnahtar":"..."}'` — düz nesne, string değerler.
   */
  @property({ type: String, attribute: "variant-attributes" })
  variantAttributesJson = "";

  /**
   * Yalnızca JS: `el.defaultAttributes = { ... }` — `variant-attributes` ile aynı anahtarda üzerine yazar.
   */
  @property({ type: Object })
  defaultAttributes: Record<string, string> = {};

  @property({ type: Object, attribute: "variant" })
  private _variant?: ConfiguratorVariant;

  @query('slot[name="loading"]')
  private _loadingSlot?: HTMLSlotElement;

  @query("configurator-viewer")
  private _configuratorViewer?: SugarConfiguratorViewerEntity;

  private _configuratorReady = false;
  private _firstSceneUpdated = false;
  private _initialLoadDone = false;

  get configurator(): SugarConfiguratorViewerEntity | undefined {
    return this._configuratorViewer;
  }

  private _parseVariantAttributesJson(): Record<string, string> {
    const raw = this.variantAttributesJson?.trim() ?? "";
    if (!raw) return {};
    try {
      const o = JSON.parse(raw) as unknown;
      if (!o || typeof o !== "object" || Array.isArray(o)) {
        logger.warn("model-viewer: variant-attributes JSON nesne olmalı", { raw });
        return {};
      }
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
        if (v == null) continue;
        const s = String(v).trim();
        if (s !== "") out[k] = s;
      }
      return out;
    } catch (e) {
      logger.warn("model-viewer: variant-attributes parse hatası", { raw, e });
      return {};
    }
  }

  /**
   * 1) `variant-attributes` JSON  2) `defaultAttributes`  3) `_variant.attributes` (variant / changeVariant)
   */
  private _composeVariant(): ConfiguratorVariant {
    const attrs: Record<string, string> = {
      ...this._parseVariantAttributesJson(),
      ...(this.defaultAttributes ?? {}),
    };

    if (this._variant?.attributes) {
      Object.assign(attrs, this._variant.attributes);
    }

    const name =
      this._variant?.name?.trim() || attrs.body || attrs.name || "model-viewer";

    const variant: ConfiguratorVariant = {
      variantId: this._variant?.variantId,
      name,
      explanation: this._variant?.explanation ?? "",
      urlHandle: this._variant?.urlHandle,
      productId: this._variant?.productId,
      attributes: attrs,
    };
    console.log("variant", variant);
    return variant;
  }

  /** `changeVariant` veya özellik güncellemelerinden sonra sahneye iletir. */
  private _applyComposedVariant() {
    this._applyVariantPendingLoading();
    waitTill(() => this._configuratorReady).then(() => {
      const composed = this._composeVariant();
      if (this._configuratorViewer) {
        this._configuratorViewer.variant = composed;
      }
    });
  }

  /**
   * Mevcut varyant üzerinde `attributes` alanını birleştirip `changeVariant` (yeniden yükleme) tetikler.
   */
  changeVariant(partial: Record<string, string>) {
    if (!this._variant) {
      this._variant = {
        name: "",
        explanation: "",
        attributes: {},
      };
    }
    if (!this._variant.attributes) this._variant.attributes = {};
    Object.assign(this._variant.attributes, partial);
    this._applyComposedVariant();
    this.requestUpdate();
  }

  private _emitLoading(phase: ModelViewerLoadingPhase, extra?: Record<string, unknown>) {
    this.dispatchEvent(
      new CustomEvent("model-viewer-loading", {
        bubbles: true,
        composed: true,
        detail: { phase, ...extra },
      }),
    );
  }

  /** Varsayılan loading slot içeriği için; atanmış light DOM yoksa fallback Düğümleri döner. */
  private _defaultLoadingPair(): {
    primary?: HTMLElement;
    secondary?: HTMLElement;
  } {
    const slot = this._loadingSlot;
    if (!slot) return {};
    const assigned = slot.assignedElements({ flatten: true }) as HTMLElement[];
    if (assigned.length >= 2) {
      return { primary: assigned[0], secondary: assigned[1] };
    }
    if (assigned.length === 1) {
      return { primary: assigned[0] };
    }
    const wrap = slot.querySelector(".mv-default-loading");
    if (!wrap) return {};
    return {
      primary: wrap.querySelector("loading-element") as HTMLElement | undefined,
      secondary: wrap.querySelector("loading-element2") as HTMLElement | undefined,
    };
  }

  private _applyInitialLoadingAfterFetch() {
    const { primary, secondary } = this._defaultLoadingPair();
    if (secondary) secondary.style.display = "";
    if (primary) {
      primary.style.display = "";
      primary.style.opacity = "0";
    }
    this._emitLoading("fetch-done");
  }

  private _applyVariantPendingLoading() {
    const { primary } = this._defaultLoadingPair();
    if (primary) primary.style.display = "";
    this._emitLoading("variant-pending");
  }

  private _applySceneReadyLoading() {
    const { primary, secondary } = this._defaultLoadingPair();
    if (primary) primary.style.display = "none";
    setTimeout(() => {
      if (secondary) secondary.style.display = "none";
      if (primary) primary.style.opacity = "1";
      this._emitLoading("scene-ready");
      if (!this._firstSceneUpdated) {
        setTimeout(() => {
          Engine.activeEngine?.dispatchCustomEvent("scene-first-updated");
          this._firstSceneUpdated = true;
        }, 1000);
      }
    }, 20);
  }

  set variant(value: ConfiguratorVariant | undefined) {
    this._variant = value
      ? {
          ...value,
          attributes: value.attributes ? { ...value.attributes } : {},
        }
      : undefined;
    this._applyComposedVariant();
  }

  get variant(): ConfiguratorVariant | undefined {
    return this._variant;
  }

  set showMeter(value: boolean) {
    if (this._configuratorViewer) {
      this._configuratorViewer.showMeter = value;
    }
    this.requestUpdate();
  }

  get showMeter(): boolean {
    return this._configuratorViewer?.showMeter ?? false;
  }

  toggleMeter() {
    if (this._configuratorViewer?.variant) {
      this._configuratorViewer.showMeter = !this._configuratorViewer.showMeter;
    }
    this.requestUpdate();
  }

  /**
   * Aktif kameraya dokunmadan, verilen preset/açı ve boyutta snapshot blob'u
   * üretir. Snapshot tek seferlik off-cycle bir render geçişidir; smooth-orbit
   * animasyonu, `fit-camera`/`camera-end` event akışı tetiklenmez.
   */
  async takeSnapshot(
    preset: Parameters<SugarConfiguratorViewerEntity["renderSnapshot"]>[0],
    options: Parameters<SugarConfiguratorViewerEntity["renderSnapshot"]>[1],
  ): Promise<Blob | null> {
    if (!this._configuratorViewer) return null;
    return this._configuratorViewer.renderSnapshot(preset, options);
  }

  private parseConfiguratorNumericId(): number {
    const raw = this.configuratorId?.trim() ?? "";
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) {
      logger.warn("model-viewer: geçersiz configurator-id, 24 kullanılıyor", {
        configuratorId: this.configuratorId,
      });
      return 24;
    }
    return n;
  }

  private async loadConfiguratorData(): Promise<void> {
    this._emitLoading("fetch-start");
    const id = this.parseConfiguratorNumericId();
    const data = await Api.fetchConfigurator(id);
    setConfiguratorData(this.configuratorId, data);
  }

  async firstUpdated() {
    if (this._configuratorViewer) {
      this._configuratorViewer.configuratorId = this.configuratorId;
    }
    await this.loadConfiguratorData();

    this._applyInitialLoadingAfterFetch();
    this.dispatchEvent(new CustomEvent("cofigurator-ready", { bubbles: true, composed: true }));

    this.shadowRoot?.addEventListener("configurator-updated", () => {
      this._applySceneReadyLoading();
      this.dispatchEvent(
        new CustomEvent("configurator-updated", { bubbles: true, composed: true }),
      );
    });

    this._configuratorReady = true;

    this._applyComposedVariant();
    this._initialLoadDone = true;
  }

  override updated(changed: Map<string | number | symbol, unknown>) {
    super.updated(changed);
    if (changed.has("configuratorId") && this._initialLoadDone) {
      void this._reloadAfterConfiguratorIdChange();
      return;
    }
    if (!this._configuratorReady) return;
    if (
      changed.has("variantAttributesJson") ||
      changed.has("defaultAttributes")
    ) {
      this._applyComposedVariant();
    }
  }

  private async _reloadAfterConfiguratorIdChange() {
    if (this._configuratorViewer) {
      this._configuratorViewer.configuratorId = this.configuratorId;
    }
    await this.loadConfiguratorData();
    this._applyComposedVariant();
    this.requestUpdate();
  }

  protected renderHtml(): TemplateResult {
    return html`
      <div class="model-viewer-root">
        <div class="mv-loading-layer">
          <slot name="loading">
            <div class="mv-default-loading">
              <loading-element style="opacity: 0;"></loading-element>
              <loading-element2 text="3D Model Yükleniyor..."></loading-element2>
            </div>
          </slot>
        </div>
        <sugar-canvas>
          <smooth-orbit></smooth-orbit>
          
          <configurator-viewer>
            <gtao-postprocess></gtao-postprocess>
            <camera-animation></camera-animation>
          </configurator-viewer>
          <div class="model-viewer-overlay-stack">
            <slot name="controls">
            </slot>
            <slot name="overlay"></slot>
          </div>
        </sugar-canvas>
      </div>
    `;
  }
}
